import React, {
  useCallback, useEffect, useMemo, useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { contactItems } from '@/utils/consts';
import { useWidthApp } from '@/utils/hooks';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import Newsletter from '@/components/Newsletter';
import Accordion from '@/components/Accordion';

import { getMenuItems, infoItems } from './utils';
import {
  MenuContainer, ItemContainer, Item, StyledLink, BottomContainer, CopyRight, FootWrapper, Title,
} from './styles';

const Menu = ({ isOpenMenu, toggleMenu, uri }) => {
  const ref = useRef();
  const { t } = useTranslation(['app', 'footer']);
  const { width, isMobile } = useWidthApp();
  const isCriticWidth = useMemo(() => width < 1260, [width]);
  const checkClick = useCallback((e) => {
    const elem = e.target;

    if (elem.tagName === 'A' && elem.selected) {
      toggleMenu(false);
    }
    if (elem.closest('#menuButton') || elem.closest('#menuBody')) return;

    toggleMenu(false);
  }, [toggleMenu]);

  const menu = useMemo(() => getMenuItems(isMobile, isCriticWidth), [isMobile, isCriticWidth]);

  useEffect(() => {
    if (isOpenMenu) {
      window.document.body.addEventListener('click', checkClick);
    }
    return () => {
      if (isOpenMenu) {
        window.document.body.removeEventListener('click', checkClick);
      }
    };
  }, [checkClick, isOpenMenu]);

  useEffect(() => {
    if (isOpenMenu && isMobile && ref.current?.scrollTop) {
      ref.current.scroll(0, 0);
    }
  }, [isMobile, isOpenMenu]);

  return (
    <MenuContainer
      id="menuBody"
      isOpen={isOpenMenu}
      pose={isOpenMenu ? 'enter' : 'exit'}
      initialPose="exit"
      ref={ref}
    >
      <div>
        {menu.map(({ title, link }) => (
          <ItemContainer key={link} amount={menu.length}>
            <Item to={link} selected={uri.includes(link)}>
              {t(title)}
            </Item>
          </ItemContainer>
        ))}
        {isMobile && (
          <>
            <Accordion title={<Title>{t('contact')}</Title>}>
              {contactItems.map(({ title, link }) => (
                <StyledLink
                  key={link}
                  to={link}
                  selected={uri === link}
                >
                  {t(title)}
                </StyledLink>
              ))}
            </Accordion>
            <Accordion title={<Title>{t('info')}</Title>}>
              {infoItems.map(({ title, link }) => (
                <StyledLink
                  key={link}
                  to={link}
                  selected={uri === link}
                >
                  {t(title)}
                </StyledLink>
              ))}
            </Accordion>
          </>
        )}
      </div>
      {isMobile && (
        <BottomContainer>
          <Newsletter />
          <FootWrapper>
            <CopyRight>
              {`© ${new Date().getFullYear()}`}
              {' '}
              {t('footer:medignition')}
            </CopyRight>
            <LanguageSwitcher />
          </FootWrapper>
        </BottomContainer>
      )}
    </MenuContainer>
  );
};

Menu.propTypes = {
  isOpenMenu: PropTypes.bool.isRequired,
  toggleMenu: PropTypes.func.isRequired,
  uri: PropTypes.string.isRequired,
};

export default Menu;
